import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Plus, Edit2, Trash2, Calendar, Users, MapPin, Mail, Phone, Clock, RefreshCw, Filter } from 'lucide-react';
import { format, parseISO, addDays, isAfter, isBefore, startOfToday } from 'date-fns';
import { dateLocales } from '../i18n';
import BookingForm from '../components/BookingForm';
import BookingDetailsModal from '../components/BookingDetailsModal';
import { useNotification } from '../contexts/NotificationContext';
import { logActivity } from '../utils/activityLogger';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';

interface Booking {
  id: string;
  property: {
    id: string;
    name: string;
    address: string;
  };
  guest_first_name: string;
  guest_last_name: string;
  guest_count: number;
  check_in_date: string;
  check_out_date: string;
  cleaning_service: boolean;
  bed_sheets_service: boolean;
  guest_email?: string;
  guest_phone?: string;
  door_code?: string;
  comments?: string;
}

interface Property {
  id: string;
  name: string;
}

export default function Bookings() {
  const { t, i18n } = useTranslation();
  const { userRole } = useAuth();
  const { addNotification } = useNotification();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [selectedServices, setSelectedServices] = useState<'cleaning' | 'bedsheets' | 'both' | null>(null);
  const [dateRange, setDateRange] = useState<'upcoming' | 'all'>('upcoming');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isSupport = userRole === 'support';
  const canManageBookings = ['owner', 'admin', 'super_admin'].includes(userRole || '');

  const fetchProperties = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name')
        .order('name');

      if (error) throw error;
      if (data) {
        setProperties(data);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      setError(null);
      setIsRefreshing(true);

      let query = supabase
        .from('bookings')
        .select(`
          id,
          property:properties(id, name, address),
          guest_first_name,
          guest_last_name,
          guest_count,
          check_in_date,
          check_out_date,
          cleaning_service,
          bed_sheets_service,
          guest_email,
          guest_phone,
          door_code,
          comments
        `)
        .order('check_in_date', { ascending: true });

      if (selectedProperty) {
        query = query.eq('property_id', selectedProperty);
      }

      if (selectedServices === 'cleaning') {
        query = query.eq('cleaning_service', true);
      } else if (selectedServices === 'bedsheets') {
        query = query.eq('bed_sheets_service', true);
      } else if (selectedServices === 'both') {
        query = query.eq('cleaning_service', true).eq('bed_sheets_service', true);
      }

      const today = startOfToday();
      const todayStr = format(today, 'yyyy-MM-dd');

      if (isSupport) {
        const sevenDaysFromNow = format(addDays(today, 7), 'yyyy-MM-dd');
        query = query.or(
          `and(check_in_date.lt.${todayStr},check_out_date.gte.${todayStr}),` +
          `and(check_in_date.gte.${todayStr},check_in_date.lte.${sevenDaysFromNow})`
        );
      } else if (dateRange === 'upcoming') {
        query = query.or(
          `and(check_in_date.lt.${todayStr},check_out_date.gte.${todayStr}),` +
          `check_in_date.gte.${todayStr}`
        );
      }

      const { data, error } = await query;

      if (error) throw error;
      if (data) {
        const sortedBookings = data.sort((a, b) => {
          const today = startOfToday();
          const isCurrentA = parseISO(a.check_in_date) <= today && parseISO(a.check_out_date) >= today;
          const isCurrentB = parseISO(b.check_in_date) <= today && parseISO(b.check_out_date) >= today;
          
          if (isCurrentA && !isCurrentB) return -1;
          if (!isCurrentA && isCurrentB) return 1;
          
          return parseISO(a.check_in_date).getTime() - parseISO(b.check_in_date).getTime();
        });
        
        setBookings(sortedBookings);
      }
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      setError(error.message);
      addNotification('error', t('bookings.notifications.error'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [addNotification, isSupport, selectedProperty, selectedServices, dateRange, t]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useKeyboardShortcut('n', () => {
    if (canManageBookings) {
      setSelectedBooking(null);
      setShowForm(true);
    }
  }, true);

  const handleDelete = async (id: string) => {
    setBookingToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) return;

    try {
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', bookingToDelete);

      if (error) throw error;

      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (userId) {
        await logActivity({
          user_id: userId,
          action: 'delete',
          entity_type: 'booking',
          entity_id: bookingToDelete
        });
      }

      addNotification('success', t('bookings.notifications.deleted'));
      fetchBookings();
    } catch (error: any) {
      console.error('Error deleting booking:', error);
      addNotification('error', t('bookings.notifications.error'));
    } finally {
      setShowDeleteConfirm(false);
      setBookingToDelete(null);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {t('bookings.title')}
        </h1>
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <button
            onClick={() => fetchBookings()}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            disabled={isRefreshing}
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
          {canManageBookings && (
            <button
              onClick={() => {
                setSelectedBooking(null);
                setShowForm(true);
              }}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 w-full sm:w-auto justify-center"
            >
              <Plus size={20} />
              <span>{t('bookings.new')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('common.property')}
          </label>
          <select
            value={selectedProperty || ''}
            onChange={(e) => setSelectedProperty(e.target.value || null)}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">{t('common.all')}</option>
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('bookings.filters.services')}
          </label>
          <select
            value={selectedServices || ''}
            onChange={(e) => setSelectedServices(e.target.value as any)}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">{t('bookings.filters.allServices')}</option>
            <option value="cleaning">{t('bookings.filters.cleaning')}</option>
            <option value="bedsheets">{t('bookings.filters.bedSheets')}</option>
            <option value="both">{t('bookings.filters.both')}</option>
          </select>
        </div>

        {!isSupport && (
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.period')}
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as 'upcoming' | 'all')}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="upcoming">{t('bookings.filters.currentAndUpcoming')}</option>
              <option value="all">{t('bookings.filters.allBookings')}</option>
            </select>
          </div>
        )}
      </div>

      {/* Mobile View */}
      <div className="block lg:hidden space-y-4">
        {bookings.map((booking) => {
          const isCurrentStay = parseISO(booking.check_in_date) <= startOfToday() && 
                              parseISO(booking.check_out_date) >= startOfToday();
          const isPastStay = parseISO(booking.check_out_date) < startOfToday();

          return (
            <div 
              key={booking.id}
              className={`bg-white rounded-lg shadow p-4 ${
                isCurrentStay ? 'border-l-4 border-green-500' :
                isPastStay ? 'border-l-4 border-gray-300' : ''
              }`}
              onClick={() => setSelectedBooking(booking)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {booking.property.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {booking.property.address}
                  </p>
                </div>
                {canManageBookings && (
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBooking(booking);
                        setShowForm(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(booking.id);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users size={16} className="text-gray-400" />
                  <span>
                    {booking.guest_first_name} {booking.guest_last_name} ({booking.guest_count} {t('common.people')})
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar size={16} className="text-gray-400" />
                  <span>
                    {format(parseISO(booking.check_in_date), 'P', { locale: dateLocales[i18n.language] })}
                    {' → '}
                    {format(parseISO(booking.check_out_date), 'P', { locale: dateLocales[i18n.language] })}
                  </span>
                </div>

                {booking.guest_email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail size={16} className="text-gray-400" />
                    <a href={`mailto:${booking.guest_email}`} className="hover:text-blue-600" onClick={(e) => e.stopPropagation()}>
                      {booking.guest_email}
                    </a>
                  </div>
                )}

                {booking.guest_phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone size={16} className="text-gray-400" />
                    <a href={`tel:${booking.guest_phone}`} className="hover:text-blue-600" onClick={(e) => e.stopPropagation()}>
                      {booking.guest_phone}
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                {booking.cleaning_service && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {t('bookings.filters.cleaning')}
                  </span>
                )}
                {booking.bed_sheets_service && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {t('bookings.filters.bedSheets')}
                  </span>
                )}
                {isCurrentStay && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {t('bookings.status.current')}
                  </span>
                )}
                {isPastStay && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {t('bookings.status.past')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {bookings.length === 0 && (
          <div className="text-center py-8 text-gray-500 bg-white rounded-lg shadow">
            {t('common.noData')}
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.property')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.guests')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.services')}
                </th>
                {canManageBookings && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bookings.map((booking) => {
                const isCurrentStay = parseISO(booking.check_in_date) <= startOfToday() && 
                                    parseISO(booking.check_out_date) >= startOfToday();
                const isPastStay = parseISO(booking.check_out_date) < startOfToday();
                
                return (
                  <tr 
                    key={booking.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${
                      isCurrentStay ? 'bg-green-50' : 
                      isPastStay ? 'bg-gray-50' : ''
                    }`}
                    onClick={() => setSelectedBooking(booking)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {booking.property.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {booking.property.address}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {booking.guest_first_name} {booking.guest_last_name}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <Users size={16} className="mr-1" />
                        {booking.guest_count} {t('common.people')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {format(parseISO(booking.check_in_date), 'P', { locale: dateLocales[i18n.language] })}
                        {' → '}
                        {format(parseISO(booking.check_out_date), 'P', { locale: dateLocales[i18n.language] })}
                      </div>
                      {isCurrentStay && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t('bookings.status.current')}
                        </span>
                      )}
                      {isPastStay && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {t('bookings.status.past')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        {booking.cleaning_service && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {t('bookings.filters.cleaning')}
                          </span>
                        )}
                        {booking.bed_sheets_service && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {t('bookings.filters.bedSheets')}
                          </span>
                        )}
                      </div>
                    </td>
                    {canManageBookings && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBooking(booking);
                            setShowForm(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                          title={t('common.edit')}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(booking.id);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title={t('common.delete')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {bookings.length === 0 && (
                <tr>
                  <td
                    colSpan={canManageBookings ? 5 : 4}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <BookingForm
          booking={selectedBooking}
          onClose={() => {
            setShowForm(false);
            setSelectedBooking(null);
          }}
          onSuccess={fetchBookings}
        />
      )}

      {selectedBooking && !showForm && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('bookings.delete')}
        message={t('bookings.deleteConfirm')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}